import type { PaymentBillingDetailsModel } from '@supply-mover-2-0/shared';
import { normalizePaymentBillingDetails } from '@supply-mover-2-0/shared';

/**
 * PD-17281 — a card entered at checkout that the customer chose NOT to save.
 *
 * The checkout keeps this instead of a saved payment method id and sends it
 * with Place Order. The server places the hold with the gateway token and,
 * on CyberSource, asks for a reusable token for the card in the same request
 * so the order can still be charged when it ships. Nothing is written to the
 * customer's saved payment methods.
 */
export interface OneTimeCardSelectionModel {
	/** The gateway's short-lived token for the card, from the payment form. */
	opaqueData: { dataDescriptor: string; dataValue: string };
	/** Network name ('Visa', 'MasterCard', ...) when the form detected it. */
	paymentMethodSubtype?: string;
	paymentMethodLastFour?: string;
	/** Name on the card. */
	paymentMethodName?: string;
	billingDetails: PaymentBillingDetailsModel | null;
	/**
	 * When the token stops being usable (epoch milliseconds). Place Order
	 * checks it first, so a customer who left the page open is asked to enter
	 * the card again instead of meeting a gateway refusal.
	 */
	expiresAt: number;
}

/**
 * CyberSource's transient tokens are valid for 15 minutes from creation. Used
 * when the token itself does not say (it normally carries `exp`).
 */
export const ONE_TIME_CARD_TOKEN_LIFETIME_MS = 15 * 60 * 1000;

/**
 * Treated as expired this long before the real expiry, so a token that is
 * nearly out of time when Place Order is pressed is not sent to a gateway
 * that will refuse it while the order is being placed.
 */
const EXPIRY_SAFETY_MARGIN_MS = 60 * 1000;

/** The JWT payload fields this file reads; everything else is ignored. */
interface CyberSourceTransientTokenPayloadModel {
	exp?: unknown;
	content?: {
		paymentInformation?: {
			card?: { number?: { maskedValue?: unknown } };
		};
	};
	data?: { number?: unknown };
}

const decodeBase64Url = (value: string): string => {
	const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
	const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
	return atob(padded);
};

const lastFourOf = (value: unknown): string | null => {
	if (typeof value !== 'string') {
		return null;
	}
	const match = /(\d{4})$/.exec(value);
	return match?.[1] ?? null;
};

/**
 * What a CyberSource transient token (the Flex Microform JWT) says about the
 * card: its last four digits and when it expires. The hosted card field
 * never exposes the number to the page, and the token is the only place the
 * masked number is available before the order is placed. Both come back null
 * for anything that is not a readable JWT; nothing here verifies the
 * signature, because nothing here is trusted beyond what is displayed.
 */
export const readCyberSourceTransientToken = (
	token: string,
): { lastFour: string | null; expiresAt: number | null } => {
	const encodedPayload = token.split('.')[1];
	if (encodedPayload == null || encodedPayload === '') {
		return { lastFour: null, expiresAt: null };
	}

	try {
		const payload = JSON.parse(
			decodeBase64Url(encodedPayload),
		) as CyberSourceTransientTokenPayloadModel;

		const lastFour =
			lastFourOf(payload.content?.paymentInformation?.card?.number?.maskedValue) ??
			lastFourOf(payload.data?.number);
		const expiresAt =
			typeof payload.exp === 'number' && Number.isFinite(payload.exp)
				? payload.exp * 1000
				: null;

		return { lastFour, expiresAt };
	} catch {
		return { lastFour: null, expiresAt: null };
	}
};

/**
 * Build the checkout's one-time card from what the payment form produced.
 * The last four and the expiry are read from a CyberSource token when the
 * form could not supply them.
 */
export const buildOneTimeCardSelection = (input: {
	opaqueData: { dataDescriptor: string; dataValue: string };
	paymentMethodSubtype?: string;
	paymentMethodLastFour?: string;
	paymentMethodName?: string;
	billingDetails: PaymentBillingDetailsModel;
	now?: number;
}): OneTimeCardSelectionModel => {
	const now = input.now ?? Date.now();
	const fromToken = readCyberSourceTransientToken(input.opaqueData.dataValue);
	const lastFour =
		input.paymentMethodLastFour != null && input.paymentMethodLastFour !== ''
			? input.paymentMethodLastFour
			: fromToken.lastFour;
	const name =
		input.paymentMethodName != null && input.paymentMethodName.trim() !== ''
			? input.paymentMethodName.trim()
			: undefined;

	return {
		opaqueData: input.opaqueData,
		...(input.paymentMethodSubtype != null && input.paymentMethodSubtype !== ''
			? { paymentMethodSubtype: input.paymentMethodSubtype }
			: {}),
		...(lastFour != null ? { paymentMethodLastFour: lastFour } : {}),
		...(name != null ? { paymentMethodName: name } : {}),
		billingDetails: normalizePaymentBillingDetails(input.billingDetails),
		expiresAt: fromToken.expiresAt ?? now + ONE_TIME_CARD_TOKEN_LIFETIME_MS,
	};
};

/** Whether the card's token is too close to expiry to send with an order. */
export const isOneTimeCardExpired = (
	card: Pick<OneTimeCardSelectionModel, 'expiresAt'>,
	now: number = Date.now(),
): boolean => now >= card.expiresAt - EXPIRY_SAFETY_MARGIN_MS;

/**
 * The order-submit fields for a one-time card, the same for the storefront
 * and the CRM submit bodies: the token, what the transaction row shows about
 * the card, and the billing details the gateway needs to keep a reusable
 * token for it.
 */
export const oneTimeCardSubmitFields = (
	card: OneTimeCardSelectionModel,
): {
	opaqueData: { dataDescriptor: string; dataValue: string };
	paymentMethodSubtype?: string;
	paymentMethodLastFour?: string;
	paymentMethodName?: string;
	billingDetails?: PaymentBillingDetailsModel;
} => ({
	opaqueData: card.opaqueData,
	...(card.paymentMethodSubtype != null
		? { paymentMethodSubtype: card.paymentMethodSubtype }
		: {}),
	...(card.paymentMethodLastFour != null
		? { paymentMethodLastFour: card.paymentMethodLastFour }
		: {}),
	...(card.paymentMethodName != null
		? { paymentMethodName: card.paymentMethodName }
		: {}),
	...(card.billingDetails != null
		? { billingDetails: card.billingDetails }
		: {}),
});
